/**
 * Diversity Stage — Stage 5c: Question type diversity validation.
 *
 * ARCHITECTURE v4.1 — Sprint 7 (diversity refactor) — P1-3 fix mei 2026
 *
 * Validates family-level question type diversity to prevent over-reliance
 * on safe/monotone question types (MC, fill_in, true_false_series).
 *
 * Runs as a BATCH check (like visualConsistencyBatch) — groups questions
 * by familyId and checks each family for diversity rules.
 *
 * DOMAIN (P1-3, mei 2026): issues are emitted under domain 'diversity'
 * (was 'support' historically). diversityStage is its own pipeline concern
 * — admin filters previously showed diversity-issues under the support-tab,
 * which lied about the actual concern.
 *
 * Active rules (from docs/guides/QUESTION_TYPE_DIVERSITY_RULES.md):
 * - DIV-001: ≥ 3 different question types per family (coach)
 * - DIV-002: ≥ 1 interactive type (not MC or fill_in) (coach)
 * - DIV-003: family-level type overuse — generalized rollup (coach/info)
 *   Replaces legacy DIV-003 (MC ≤ 50%) and DIV-007 (fill_in ≤ 30%).
 *   Emits DIVERSITY_TYPE_OVERUSE with metadata.reason = questionType.
 *   Per-type caps live in TYPE_OVERUSE_THRESHOLDS below — extend that map
 *   to add caps for new types without touching this stage's logic.
 * - DIV-004: ≥ 8 questions → ≥ 4 types (coach)
 * - DIV-005: ≥ 1 DOK3-capable type recommended (info)
 *   DOK3-capable set is DERIVED from questionTypeDefinitions.ts via
 *   cognitiveCeiling === 'DOK3' — adding a new DOK3 type to that registry
 *   automatically enrolls it here. No hardcoded list to drift.
 *
 * Removed rules (Sprint 7):
 * - DIV-006 (consecutive same type) — false signal: it checked the order
 *   of questions in the seed JSON, but the adaptive engine (v11.2) and
 *   mode selectors shuffle and dynamically select from the pool at
 *   runtime, so seed order never reaches the learner.
 *
 * Scalability: INTERACTIVE_TYPES and DOK3_TYPES are no longer hardcoded.
 * - INTERACTIVE_TYPES derives from the RUNTIME registry's `isInteractive`
 *   flag (QUESTION_TYPE_META) — adding a new interactive renderer auto-
 *   enrolls it here. No governance sidecar update needed.
 * - DOK3_TYPES derives from `cognitiveCeiling === 'DOK3'` in the governance
 *   sidecar (questionTypeDefinitions.ts) — a didactic decision, not a
 *   runtime property, so it stays in the sidecar.
 *
 * All rules start as coach/info. Promotion to blocker planned for Phase 3 (Aug 2026).
 *
 * @see docs/guides/QUESTION_TYPE_DIVERSITY_RULES.md
 * @see src/governance/questionTypeDefinitions.ts (SSOT for cognitiveCeiling)
 */

import type { ValidationIssue } from '../issueContract';
import { coach, info } from '../issueContract';
import type { Question, QuestionType } from '../../../content';
import { STAGES } from '../stageDefinitions';
import { getDok3CapableTypes } from '../../questionTypeDefinitions';
import { QUESTION_TYPE_META } from '../../../content/questions/questionTypeRegistry';
import { getPreferredQuestionTypes } from '../policyEngine';

// SSOT for stage numbers — see stageDefinitions.ts (G-15 fix, RC-3)
const STAGE = STAGES.DIVERSITY;

// ─── Derived Type Sets ────────────────────────────────────────────────────────

/**
 * Types that count as "interactive" for diversity purposes.
 *
 * SSOT: the runtime registry's `isInteractive` flag
 * (src/content/questions/questionTypeRegistry.ts). Any type registered
 * there as interactive automatically counts here — no governance sidecar
 * update needed.
 *
 * Exception: `fill_in` is interactive at the renderer level but
 * pedagogically passive/reproductive (single numeric answer), so it is
 * explicitly excluded from "interactive" for diversity purposes.
 */
const INTERACTIVE_TYPES: ReadonlySet<string> = new Set(Object.values(QUESTION_TYPE_META).filter(m => m.isInteractive && m.type !== 'fill_in').map(m => m.type));

/**
 * Types that the diversity stage treats as DOK3-capable.
 * Derived from cognitiveCeiling === 'DOK3' in questionTypeDefinitions.ts.
 * Update the ceiling there to enroll/remove a type from this rule.
 */
const DOK3_TYPES: ReadonlySet<string> = new Set(getDok3CapableTypes());

// ─── Type-Overuse Thresholds ──────────────────────────────────────────────────

/**
 * Per-type maximum share within a family (0.0–1.0).
 *
 * Rationale: low-cognitive types (recognition / single-pick / binary)
 * need a cap because over-reliance kills variety and depth. Higher-DOK
 * types do not need a cap — diversity itself solves their overuse.
 *
 * To add a cap for a new type, add an entry here. The stage emits one
 * DIVERSITY_TYPE_OVERUSE issue per offending entry, with metadata.reason
 * = the question type, so admins/UI can group findings by reason.
 */
const TYPE_OVERUSE_THRESHOLDS: Readonly<Partial<Record<QuestionType, number>>> = {
  multiple_choice: 0.5,
  fill_in: 0.3,
  true_false_series: 0.3
};

// ─── Production vs. Recognition Classification (DIV-007) ──────────────────────

/**
 * "Production" types: the learner GENERATES the answer surface — they type the
 * word, produce the spelling, build the structure, or actively construct the
 * solution. These are the antidote to dictatish clustering.
 *
 * Curated (not a runtime flag) because production-vs-recognition is a
 * didactic property, not a render property. Anything NOT listed here is
 * treated as recognition (the learner selects/judges among given material:
 * multiple_choice, categorize, true_false_series, comparison_judge, matching,
 * sentence_error_judge, text_highlight, *_reading, *_chart, …).
 *
 * To enroll a new production type, add it here.
 */
const PRODUCTION_TYPES: ReadonlySet<QuestionType> = new Set<QuestionType>(['fill_in', 'spelling_pick', 'verb_ending', 'word_builder', 'error_spotting', 'drag_drop_fill', 'step_solver', 'sentence_parser', 'verb_conjugation_flow', 'cijferend_rekenen', 'cijferend_delen', 'place_value_build', 'square_root_builder', 'scientific_notation_builder', 'fraction_strip_builder', 'comma_shift', 'clock_set', 'money', 'ordering', 'number_sequence', 'grid_shade', 'bar_model_part']);

/** Family-skew threshold: recognition share at/above this is "dictatish". */
const RECOGNITION_SKEW_THRESHOLD = 0.75;
/** Minimum family size before the skew signal is meaningful. */
const RECOGNITION_SKEW_MIN_SIZE = 5;

// ─── Thresholds ───────────────────────────────────────────────────────────────

/** Configurable thresholds for diversity checks */
export const DIVERSITY_THRESHOLDS = {
  /** Minimum different question types per family */
  minTypes: 3,
  /** Minimum types for families with ≥ 8 questions */
  minTypesLargeFamily: 4,
  /** Large family threshold (question count) */
  largeFamilySize: 8,
  /** Minimum family size for DIV-005 (DOK3-capable type recommended) */
  dok3RecommendationMinSize: 5
} as const;

// ─── Batch Diversity Check ────────────────────────────────────────────────────

/**
 * Run diversity checks on a batch of questions, grouped by family.
 * Returns issues attached to individual questions within each family.
 */
export function runDiversityBatch(questions: Question[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // Group questions by familyId
  const families = new Map<string, Question[]>();
  for (const q of questions) {
    const fId = q.familyId ?? 'UNKNOWN';
    const existing = families.get(fId) ?? [];
    existing.push(q);
    families.set(fId, existing);
  }

  // Check each family
  for (const [familyId, familyQuestions] of families) {
    if (familyQuestions.length < 2) continue; // Skip tiny families

    const familyIssues = checkFamilyDiversity(familyId, familyQuestions);
    issues.push(...familyIssues);
  }
  return issues;
}

// ─── Family-Level Checks ──────────────────────────────────────────────────────

/**
 * Generic fallback suggestion lists — used when a family has no rulebook
 * or its rulebook does not list useful preferredTypes. Kept deliberately
 * broad so they remain accurate across all domains.
 */
const GENERIC_INTERACTIVE_FALLBACK = ['ordering', 'matching', 'categorize', 'error_spotting', 'true_false_series', 'step_solver', 'comparison_judge'] as const;

/**
 * Build a domain-aware suggestion list for diversity issues by consulting
 * the family's module rulebook. Returns rulebook `preferredTypes` that are
 * NOT already present in the family, optionally filtered to interactive
 * types only (for DIV-002). Falls back to a generic interactive list when
 * the rulebook is missing or yields nothing useful.
 */
function buildDomainAwareSuggestion(questions: Question[], presentTypes: ReadonlySet<string>, options: {
  interactiveOnly: boolean;
}): string {
  const moduleId = questions.find(q => q.module)?.module;
  const preferred = moduleId ? getPreferredQuestionTypes(moduleId) : [];

  // Filter: not already present + (optionally) interactive-only + never
  // suggest the two passive types as a "fix".
  const candidates = preferred.filter(t => {
    if (presentTypes.has(t)) return false;
    if (t === 'multiple_choice' || t === 'fill_in') return false;
    if (options.interactiveOnly && !INTERACTIVE_TYPES.has(t)) return false;
    return true;
  });
  const baseList = candidates.length > 0 ? candidates : GENERIC_INTERACTIVE_FALLBACK.filter(t => !presentTypes.has(t));
  const list = baseList.slice(0, 6).join(', ');
  const sourceNote = candidates.length > 0 ? ' (uit rulebook preferredTypes)' : '';
  return options.interactiveOnly ? `Voeg minimaal 1 interactief type toe${sourceNote}: ${list}.` : `Voeg domein-passende vraagtypes toe${sourceNote}: ${list}.`;
}
function checkFamilyDiversity(familyId: string, questions: Question[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const firstQId = questions[0]?.id ?? 'UNKNOWN';
  const types = new Set(questions.map(q => q.questionType));
  const typeCount = types.size;
  const totalCount = questions.length;

  // Count occurrences per type (used by DIV-003 rollup)
  const countsByType = new Map<QuestionType, number>();
  for (const q of questions) {
    countsByType.set(q.questionType, (countsByType.get(q.questionType) ?? 0) + 1);
  }
  const hasInteractive = questions.some(q => INTERACTIVE_TYPES.has(q.questionType));
  const hasDok3Type = questions.some(q => DOK3_TYPES.has(q.questionType));

  // ── DIV-001: Minimum 3 types ──────────────────────────────────────────────

  if (typeCount < DIVERSITY_THRESHOLDS.minTypes) {
    issues.push(coach('diversity', STAGE, 'DIVERSITY_TOO_FEW_TYPES', `Family "${familyId}" heeft ${typeCount} vraagtype(s) (minimum: ${DIVERSITY_THRESHOLDS.minTypes}). Types: ${[...types].join(', ')}`, {
      questionId: firstQId,
      familyId,
      field: 'questionType',
      expected: `≥ ${DIVERSITY_THRESHOLDS.minTypes} verschillende vraagtypes`,
      actual: `${typeCount} type(s): ${[...types].join(', ')}`,
      suggestion: buildDomainAwareSuggestion(questions, types, {
        interactiveOnly: false
      })
    }));
  }

  // ── DIV-002: At least 1 interactive type ──────────────────────────────────

  if (!hasInteractive) {
    issues.push(coach('diversity', STAGE, 'DIVERSITY_NO_INTERACTIVE', `Family "${familyId}" heeft geen interactief vraagtype (alleen MC/fill_in)`, {
      questionId: firstQId,
      familyId,
      field: 'questionType',
      expected: '≥ 1 interactief type (niet MC of fill_in)',
      actual: `Alleen: ${[...types].join(', ')}`,
      suggestion: buildDomainAwareSuggestion(questions, types, {
        interactiveOnly: true
      })
    }));
  }

  // ── DIV-003: Type overuse (rollup) ────────────────────────────────────────
  // Emits one DIVERSITY_TYPE_OVERUSE per type that exceeds its cap.
  // metadata.reason carries the question type; metadata.percent + threshold
  // give the discriminator for admin UI breakdown.
  //
  // P5 CALIBRATION (mei 2026, GOVERNANCE_TAA_TRIAGE):
  //   Skip overuse checks for small families (< largeFamilySize = 8). At
  //   N=4-7 questions a single extra MC pushes the family over a 50% cap
  //   purely by statistical noise, generating ~15 false positives without
  //   any actionable signal.

  if (totalCount >= DIVERSITY_THRESHOLDS.largeFamilySize) {
    for (const [qType, cap] of Object.entries(TYPE_OVERUSE_THRESHOLDS) as Array<[QuestionType, number]>) {
      const count = countsByType.get(qType) ?? 0;
      if (count === 0) continue;
      const percent = count / totalCount;
      if (percent <= cap) continue;
      const percentRounded = Math.round(percent * 100);
      const capRounded = Math.round(cap * 100);
      issues.push(coach('diversity', STAGE, 'DIVERSITY_TYPE_OVERUSE', `Family "${familyId}": ${percentRounded}% ${qType} (max ${capRounded}%)`, {
        questionId: firstQId,
        familyId,
        field: 'questionType',
        expected: `≤ ${capRounded}% ${qType}`,
        actual: `${count}/${totalCount} = ${percentRounded}%`,
        suggestion: `Vervang ${qType}-vragen door interactievere types of voeg nieuwe varianten toe.`,
        reason: qType,
        percent: percentRounded,
        threshold: capRounded,
        count,
        total: totalCount
      }));
    }
  }

  // ── DIV-004: Large families need more variety ─────────────────────────────

  if (totalCount >= DIVERSITY_THRESHOLDS.largeFamilySize && typeCount < DIVERSITY_THRESHOLDS.minTypesLargeFamily) {
    issues.push(coach('diversity', STAGE, 'DIVERSITY_LOW_VARIETY', `Family "${familyId}" heeft ${totalCount} vragen maar slechts ${typeCount} types (aanbevolen: ≥ ${DIVERSITY_THRESHOLDS.minTypesLargeFamily})`, {
      questionId: firstQId,
      familyId,
      field: 'questionType',
      expected: `≥ ${DIVERSITY_THRESHOLDS.minTypesLargeFamily} types bij ≥ ${DIVERSITY_THRESHOLDS.largeFamilySize} vragen`,
      actual: `${typeCount} type(s)`,
      suggestion: 'Voeg meer variatie toe — grotere families bieden ruimte voor diverse interacties.'
    }));
  }

  // ── DIV-005: DOK3-capable type recommended ────────────────────────────────
  // DOK3_TYPES is derived from cognitiveCeiling === 'DOK3' in
  // questionTypeDefinitions.ts so this rule auto-extends to new types.

  if (!hasDok3Type && totalCount >= DIVERSITY_THRESHOLDS.dok3RecommendationMinSize) {
    issues.push(info('diversity', STAGE, 'DIVERSITY_NO_DOK3_TYPE', `Family "${familyId}" heeft geen DOK3-capabel vraagtype (zoals ${[...DOK3_TYPES].slice(0, 5).join(', ')}${DOK3_TYPES.size > 5 ? ', …' : ''})`, {
      questionId: firstQId,
      familyId,
      suggestion: 'Overweeg een analytisch type toe te voegen voor hogere cognitieve activatie.'
    }));
  }

  // ── DIV-006 REMOVED (Sprint 7) ────────────────────────────────────────────
  // The "consecutive same type" check was a false signal: it inspected the
  // order of questions in the seed JSON, but the adaptive engine (v11.2)
  // and mode selectors shuffle and dynamically pick questions from the
  // pool at runtime, so seed order never reaches the learner.

  // ── DIV-007: Production vs. recognition skew (observability) ──────────────
  // Counts how many questions ask the learner to PRODUCE the answer vs. merely
  // RECOGNIZE it among given options. A family that is overwhelmingly
  // recognition (e.g. AU-OU's "welk woord schrijf je met au?" MC cluster) is
  // dictatish: the hardest step is given away. Emitted as `info` so the skew
  // is MEASURABLE before any migration — never blocks.

  if (totalCount >= RECOGNITION_SKEW_MIN_SIZE) {
    let productionCount = 0;
    for (const q of questions) {
      if (PRODUCTION_TYPES.has(q.questionType)) productionCount++;
    }
    const recognitionCount = totalCount - productionCount;
    const recognitionShare = recognitionCount / totalCount;
    if (recognitionShare >= RECOGNITION_SKEW_THRESHOLD) {
      const sharePct = Math.round(recognitionShare * 100);
      const thresholdPct = Math.round(RECOGNITION_SKEW_THRESHOLD * 100);
      issues.push(info('diversity', STAGE, 'DIVERSITY_PRODUCTION_RECOGNITION_SKEW', `Family "${familyId}": ${sharePct}% herkenningsvragen, ${productionCount}/${totalCount} productievragen — scheve mix (max ${thresholdPct}% herkenning aanbevolen).`, {
        questionId: firstQId,
        familyId,
        field: 'questionType',
        expected: `< ${thresholdPct}% herkenningstypes`,
        actual: `${recognitionCount}/${totalCount} = ${sharePct}% herkenning`,
        suggestion: 'Voeg productievragen toe (fill_in / spelling_pick / error_spotting) zodat het kind de spelling/het antwoord zelf produceert i.p.v. herkent.',
        productionCount,
        recognitionCount,
        recognitionShare,
        threshold: RECOGNITION_SKEW_THRESHOLD
      }));
    }
  }
  return issues;
}